import {
  faLongArrowLeft,
  faLongArrowRight,
} from '@fortawesome/pro-regular-svg-icons';
import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Option } from '../../../components/form/elements/select';
import { map } from 'rxjs/operators';
import * as yup from 'yup';
import { useObservableState } from 'observable-hooks';

import Form, { SubmitType } from '../../../components/form';
import Button from '../../../components/form/elements/button';
import Group from '../../../components/form/elements/group';
import Link from '../../../components/form/elements/link';
import { LIVESTOCK_FODDER_NEED_INDEX } from '../../../router/routes';
import { useService } from '../../../services/services';
import { route } from '../../../services/utils';
import { FormData } from './create';
import { useParams } from 'react-router-dom';
import AnimalGroups from './animal-groups';
import css from './index.module.css';
import ClassifierGroup from '../../../enums/classifier-group';
import Classifier from '../../../enums/classifier';
import ClassifierPropertyKey from '../../../enums/classifier-property-key';
import AnimalGroup from '../../../interfaces/animal-group';
import ClassifierValue from '../../../interfaces/classifierValue';
import ClassifierValueProperty from '../../../interfaces/classifierValueProperty';

export interface AnimalsGroupsSubmitData {
  animalGroups: { [key: string]: { [key: string]: AnimalGroup } };
}

export default function StepAnimalGroups({
  formData,
  onReturn,
  onSubmit,
  saveFormData,
  periods,
}: {
  formData: FormData;
  onReturn: () => void;
  onSubmit: SubmitType<AnimalsGroupsSubmitData>;
  saveFormData: SubmitType<AnimalsGroupsSubmitData>;
  periods: string[];
}) {
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.livestock.fodder_need.'.concat(key)),
    [t]
  );
  const { farmId } = useParams<{ farmId: string }>();
  const { classifierService } = useService();

  const mapAnimalClassifiers = (values: ClassifierValue[]) => {
    return values.map((value: ClassifierValue) => {
      const dry: ClassifierValueProperty = Object.values(value.properties).find(
        (property: ClassifierValueProperty) => {
          return (
            property.property.key === ClassifierPropertyKey.ANIMAL_DRY_MATTER
          );
        }
      );
      return {
        value: value.id,
        label: value.value,
        dry: dry.value ?? 0,
      };
    });
  };

  const [animalSpeciesClassifiers] = useObservableState<Option[]>(
    () =>
      classifierService
        .values(ClassifierGroup.LIVESTOCK, Classifier.ANIMAL_SPECIES)
        .pipe(map(mapAnimalClassifiers)),
    null
  );

  const [animalCategoriesClassifiers] = useObservableState<Option[]>(
    () =>
      classifierService
        .values(ClassifierGroup.LIVESTOCK, Classifier.ANIMAL_CATEGORIES)
        .pipe(map(mapAnimalClassifiers)),
    null
  );

  const [body, setBody] = useState([]);

  useEffect(() => {
    if (animalSpeciesClassifiers && animalCategoriesClassifiers) {
      let animalGroupsClassifier = [
        ...animalSpeciesClassifiers,
        ...animalCategoriesClassifiers,
      ].sort((a, b) => a.label.localeCompare(b.label));

      setBody([
        Group(
          [
            AnimalGroups(
              'animalGroups',
              animalGroupsClassifier,
              saveFormData,
              periods
            ),
          ],
          tp('feedingstuffs_by_animal_groups')
        ),
      ]);
    }
  }, [
    animalSpeciesClassifiers,
    animalCategoriesClassifiers,
    periods,
    tp,
    saveFormData,
    formData,
  ]);

  const [footer] = useState(() => [
    Button('return', onReturn, tp('return'), faLongArrowLeft, null, true),
    Link(route(LIVESTOCK_FODDER_NEED_INDEX, { farmId: farmId }), 'cancel'),
    Button('continue', null, tp('continue'), faLongArrowRight),
  ]);

  const schema = (schema: any) => {
    let obj = {};
    formData.usedPeriods.forEach((element) => {
      Object.assign(obj, {
        [element]: schema.lazy((value: any) => {
          let animalGroupSchema = {};
          if (value && Object.keys(value).length > 0) {
            Object.entries(value).forEach(
              ([animalGroupKey, animlaGroup]: [string, any]) => {
                //animal group validation
                Object.assign(animalGroupSchema, {
                  [animalGroupKey]: schema.object().shape({
                    animal_group_id: schema.string().uuid(),
                    dry_intake_capacity_per_day: schema
                      .number()
                      .required()
                      .min(0),
                    animal_count: schema
                      .number()
                      .required()
                      .integer()
                      .positive(),
                    feedingstuffs: schema
                      .array()
                      .required()
                      .min(1)
                      .of(
                        yup.object().shape({
                          feedingstuff_id: schema.string().uuid(),
                          ammount_in_day_on_animal: schema
                            .number()
                            .required()
                            .min(0),
                        })
                      ),
                  }),
                });
              }
            );
            return schema.object().shape(animalGroupSchema);
          } else {
            return schema
              .object()
              .shape({})
              .test({
                test: () => false,
              });
          }
        }),
      });
    });

    return schema.object().shape({
      animalGroups: yup.object().shape(obj),
    });
  };

  return (
    <div className={css['step-animal-groups']}>
      <Form
        title={tp('fodder_need_calculation')}
        mode="step"
        body={body}
        footer={footer}
        values={formData}
        submit={onSubmit}
        schema={schema(yup)}
      />
    </div>
  );
}
